import { type Currency, RecordUnit } from '@form-factory/schema/enums';
import { formatUtils } from '@form-factory/shared';
import { Trans, useLingui } from '@lingui/react/macro';
import { Table } from '@utima/ui';

import { useEnumTranslations } from '@/hooks';
import { trpc } from '@/services/trpc';

export interface ThreadCostsProps {
  threadId: string;
  currency: Currency;
}

export function ThreadCosts({ threadId, currency }: ThreadCostsProps) {
  const { t } = useLingui();
  const { recordUnit } = useEnumTranslations();
  const { data, isLoading } = trpc.threads.getThreadCosts.useQuery({
    id: threadId,
  });

  const totalCost = data?.reduce((acc, item) => acc + item.totalCost, 0) || 0;

  const formatUnits = (units: number, unitType: RecordUnit) => {
    switch (unitType) {
      case RecordUnit.Tokens:
      case RecordUnit.Characters:
        return formatUtils.number(units);
      case RecordUnit.Seconds:
        return formatUtils.duration(units);
      case RecordUnit.Credits:
        return formatUtils.number(units);

      default:
        return formatUtils.number(units);
    }
  };

  return (
    <div className='space-y-4'>
      <div className='flex flex-col space-y-1.5'>
        <h4 className='font-semibold leading-none tracking-tight'>
          <Trans>Costs breakdown</Trans>
        </h4>
        <p className='text-sm text-muted-fg'>
          <Trans>
            Detailed breakdown of costs associated with this thread.
          </Trans>
        </p>
      </div>

      {isLoading ? (
        <div className='space-y-2'>
          <Trans>Loading</Trans>
        </div>
      ) : !data || data.length === 0 ? (
        <p className='text-muted-foreground text-sm'>
          <Trans>No usage data available for this thread.</Trans>
        </p>
      ) : (
        <>
          <Table.Root>
            <Table.Head>
              <Table.Row>
                <Table.HCol>
                  <Trans>Type</Trans>
                </Table.HCol>
                <Table.HCol className='text-right'>
                  <Trans>Units</Trans>
                </Table.HCol>
                <Table.HCol className='text-right'>
                  <Trans>Cost</Trans>
                </Table.HCol>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {data.map((item, index) => (
                <Table.Row key={`${item.type}-${item.unitType}-${index}`}>
                  <Table.Col className='font-medium'>{item.type}</Table.Col>
                  <Table.Col className='text-right'>
                    {formatUnits(item.totalUnits, item.unitType)}
                    <span className='ml-1 text-xs text-muted-fg'>
                      ({recordUnit[item.unitType].toLowerCase()})
                    </span>
                  </Table.Col>
                  <Table.Col className='text-right font-medium tabular-nums'>
                    {formatUtils.credits(item.totalCost, currency)}
                  </Table.Col>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
          <div className='mt-4 flex justify-end font-semibold'>
            <span className='mr-2 text-muted-fg'>{t`Total`}:</span>
            <span>{formatUtils.credits(totalCost, currency)}</span>
          </div>
        </>
      )}
    </div>
  );
}
